import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function About() {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotInput = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotInput && honeypotInput.value.trim() !== '') {
      setFormStatus('success');
      setFormData({ name: '', email: '', message: '' });
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
      setFormError('请填写所有必填字段');
      setFormStatus('error');
      return;
    }

    if (formData.message.length > 500) {
      setFormError('留言内容不能超过500个字符');
      setFormStatus('error');
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const payload = new FormData(form);
      const params = new URLSearchParams();
      payload.forEach((value, key) => {
        params.append(key, value.toString());
      });
      const response = await fetch('https://readdy.ai/api/form/d9onto8senbniolesij0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      const isSuccess = response.ok && parsed.code === 'OK';
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || '';

      if (isSuccess) {
        setFormStatus('success');
        setFormData({ name: '', email: '', message: '' });
        form.reset();
      } else {
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后再试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查连接后重试');
    }
  };

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '关于我们' },
  ];

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    'name': '皮皮漫画',
    'url': 'https://pipicomics.com',
    'description': '皮皮漫画提供韩漫、日漫、国漫等多种类型漫画在线免费阅读',
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': [
      { '@type': 'ListItem', 'position': 1, 'name': '首页', 'item': 'https://pipicomics.com' },
      { '@type': 'ListItem', 'position': 2, 'name': '关于我们', 'item': 'https://pipicomics.com/about' },
    ],
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    'name': '关于皮皮漫画',
    'description': '了解皮皮漫画的品牌故事、口碑评价和联系方式',
    'url': 'https://pipicomics.com/about',
    'inLanguage': 'zh-CN',
  };

  const testimonials = [
    {
      name: '小明同学',
      role: '忠实读者',
      content: '皮皮漫画真的是我用过最好的漫画阅读网站！每天都有更新，画质清晰，加载速度也很快。最爱的皮皮漫画韩漫这里都有，强烈推荐！',
      rating: 5,
    },
    {
      name: '漫画迷小王',
      role: '日漫爱好者',
      content: '作为一个资深日漫迷，皮皮漫画日漫专区简直是天堂。从经典老番到最新连载，应有尽有。而且全部免费，真的很良心！',
      rating: 5,
    },
    {
      name: '阿花',
      role: '国漫粉丝',
      content: '一直支持国漫崛起！皮皮漫画国漫分类做得特别好，很多优秀的国漫作品都能在这里找到。阅读体验一流，流量党完全不用担心。',
      rating: 5,
    },
    {
      name: '追漫达人',
      role: 'VIP读者',
      content: '皮皮漫画类型大全真的很全，从恋爱到热血，从奇幻到搞笑，想看的类型全都有。更新的速度也特别快，比很多同类平台都要好！',
      rating: 5,
    },
  ];

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutSchema) }}
      />

      <Navbar />

      <main className="pt-20">
        <section className="py-10 md:py-14 px-4 md:px-6">
          <div className="max-w-5xl mx-auto">
            <div className="mb-6">
              <Breadcrumb items={breadcrumbItems} />
            </div>

            <div className="mb-12">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4">
                关于皮皮漫画
              </h1>
              <p className="text-sm text-foreground-600 mb-2">
                皮皮漫画韩漫 · 皮皮漫画日漫 · 皮皮漫画国漫 —— 您的在线免费漫画阅读伙伴
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-16">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-1 h-5 bg-primary-500 rounded-full"></div>
                  <h2 className="text-lg font-heading font-bold text-foreground-900">
                    品牌故事
                  </h2>
                </div>
                <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
                  <p>
                    皮皮漫画诞生于对漫画的热爱。我们的创始团队是一群资深的漫画爱好者，
                    深知每一位漫画读者对于优质阅读体验的渴望。在这个信息爆炸的时代，
                    我们希望能够为大家打造一个纯净、便捷、高品质的漫画阅读空间。
                  </p>
                  <p>
                    从最初的小众社区发展到今天拥有海量<strong>皮皮漫画韩漫</strong>、
                    <strong>皮皮漫画日漫</strong>和<strong>皮皮漫画国漫</strong>的综合平台，
                    皮皮漫画始终坚持"免费、高清、快速"的核心理念。我们每日更新数百部热门作品，
                    确保读者能够第一时间追到最新话。
                  </p>
                  <p>
                    无论你是喜欢浪漫动人的韩漫、热血激情的日漫，还是精彩纷呈的国漫，
                    在<strong>皮皮漫画类型大全</strong>中都能找到属于你的那一款。
                    我们的<strong>皮皮漫画在线阅读</strong>体验经过精心优化，
                    无论是在电脑、平板还是手机上，都能获得流畅舒适的阅读感受。
                  </p>
                  <p>
                    皮皮漫画不仅是一个漫画阅读平台，更是一个连接漫画爱好者的社区。
                    我们相信，好的漫画能够跨越语言和文化的界限，触动每一个人的心灵。
                    皮皮漫画免费向所有用户开放，让更多人能够享受到漫画带来的快乐。
                  </p>
                </div>
              </div>

              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-1 h-5 bg-accent-500 rounded-full"></div>
                  <h2 className="text-lg font-heading font-bold text-foreground-900">
                    我们的优势
                  </h2>
                </div>
                <div className="space-y-4">
                  {[
                    { icon: 'ri-shield-check-line', title: '完全免费', desc: '所有漫画无需付费即可畅读，皮皮漫画免费向所有用户开放' },
                    { icon: 'ri-speed-up-line', title: '极速更新', desc: '热门作品每日更新，第一时间为读者呈现最新漫画内容' },
                    { icon: 'ri-hd-line', title: '高清画质', desc: '所有漫画均采用高清图片，给您最优质的视觉体验' },
                    { icon: 'ri-smartphone-line', title: '多端适配', desc: '支持电脑、平板、手机等多种设备，皮皮漫画在线阅读随时随地' },
                    { icon: 'ri-bookmark-line', title: '类型齐全', desc: '皮皮漫画类型大全涵盖韩漫、日漫、国漫及各类题材标签' },
                    { icon: 'ri-user-heart-line', title: '用户体验', desc: '简洁清爽的界面设计，沉浸式阅读体验，无广告干扰' },
                  ].map((item, idx) => (
                    <div key={idx} className="flex gap-3 p-4 bg-background-100 rounded-lg">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 shrink-0">
                        <i className={`${item.icon} text-lg`}></i>
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-foreground-800 mb-1">{item.title}</h3>
                        <p className="text-xs text-foreground-600 leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <section className="mb-16">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8 text-center">
                读者口碑
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {testimonials.map((t, idx) => (
                  <div key={idx} className="bg-background-100 rounded-lg p-5">
                    <div className="flex items-center gap-1 mb-2">
                      {Array.from({ length: t.rating }).map((_, i) => (
                        <i key={i} className="ri-star-fill text-accent-500 text-xs"></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-4">&quot;{t.content}&quot;</p>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-secondary-200 text-secondary-700 text-xs font-bold">
                        {t.name.charAt(0)}
                      </div>
                      <div>
                        <p className="text-xs font-medium text-foreground-800">{t.name}</p>
                        <p className="text-[11px] text-foreground-500">{t.role}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-1 h-5 bg-secondary-500 rounded-full"></div>
                    <h2 className="text-lg font-heading font-bold text-foreground-900">
                      联系我们
                    </h2>
                  </div>
                  <div className="space-y-4 text-sm text-foreground-600 mb-6">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-500">
                        <i className="ri-mail-line"></i>
                      </div>
                      <span>contact@pipicomics.com</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-full bg-accent-100 text-accent-500">
                        <i className="ri-customer-service-line"></i>
                      </div>
                      <div>
                        <p>客服工作时间</p>
                        <p className="text-xs text-foreground-500">周一至周日 9:00 - 21:00</p>
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    如果您对皮皮漫画有任何建议、意见或合作意向，欢迎随时通过以上方式联系我们。
                    我们非常重视每一位读者的反馈，您的支持是皮皮漫画不断进步的动力！
                  </p>
                </div>

                <div>
                  <form
                    data-readdy-form
                    onSubmit={handleSubmit}
                    className="bg-background-100 rounded-lg p-6 space-y-4"
                  >
                    <h3 className="text-base font-heading font-bold text-foreground-900 mb-2">
                      发送留言
                    </h3>

                    <div>
                      <label htmlFor="about-name" className="block text-xs font-medium text-foreground-700 mb-1">
                        姓名 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        id="about-name"
                        name="name"
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-md focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300"
                        placeholder="请输入您的姓名"
                      />
                    </div>

                    <div>
                      <label htmlFor="about-email" className="block text-xs font-medium text-foreground-700 mb-1">
                        邮箱 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        id="about-email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                        className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-md focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300"
                        placeholder="请输入您的邮箱"
                      />
                    </div>

                    <div>
                      <label htmlFor="about-message" className="block text-xs font-medium text-foreground-700 mb-1">
                        留言内容 <span className="text-primary-500">*</span>
                      </label>
                      <textarea
                        id="about-message"
                        name="message"
                        value={formData.message}
                        onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                        maxLength={500}
                        rows={4}
                        className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-md focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 resize-none"
                        placeholder="请输入您的留言（最多500字）"
                      ></textarea>
                      <p className="text-xs text-foreground-500 mt-1 text-right">
                        {formData.message.length}/500
                      </p>
                    </div>

                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="comic-honeypot"
                    />

                    <button
                      type="submit"
                      disabled={formStatus === 'loading'}
                      className="w-full py-3 bg-primary-500 text-background-50 text-sm font-medium rounded-full hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                    >
                      {formStatus === 'loading' ? '提交中...' : '发送留言'}
                    </button>

                    {formStatus === 'success' && (
                      <p className="text-sm text-center text-primary-600 font-medium">
                        留言发送成功！感谢您的反馈，我们会尽快回复。
                      </p>
                    )}
                    {formStatus === 'error' && formError && (
                      <p className="text-sm text-center text-red-500 font-medium">
                        {formError}
                      </p>
                    )}
                  </form>
                </div>
              </div>
            </section>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}